import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Search, Building2, Filter, X } from "lucide-react";

const CITIES = ["Toutes", "Montréal", "Laval", "Repentigny", "Lavaltrie", "Joliette", "Terrebonne", "Longueuil"];
const TYPES = [
  { v: "ALL", l: "Tous types" },
  { v: "ACTIVE_TENDER", l: "Appels d'offres actifs" },
  { v: "RFP", l: "RFP" },
  { v: "RFQ", l: "RFQ" },
  { v: "PUBLIC_CONTRACT", l: "Contrats publics" },
  { v: "PRIVATE_PROSPECT", l: "Prospects privés" },
  { v: "PROPERTY_MANAGER", l: "Gestionnaires immobiliers" },
  { v: "COMMERCIAL_BUILDING", l: "Bâtiments commerciaux" },
];
const BUILDINGS = [
  { v: "ALL", l: "Tous bâtiments" },
  { v: "OFFICE", l: "Bureaux" },
  { v: "INDUSTRIAL", l: "Industriel" },
  { v: "WAREHOUSE", l: "Entrepôts" },
  { v: "RETAIL", l: "Commerce de détail" },
  { v: "HEALTHCARE", l: "Santé" },
  { v: "EDUCATION", l: "Éducation" },
  { v: "HOSPITALITY", l: "Hôtellerie" },
  { v: "CONDOMINIUM", l: "Condominiums" },
];

const scoreColor = (s) =>
  s >= 85 ? "text-emerald-400" : s >= 70 ? "text-cyan-400" : s >= 50 ? "text-amber-400" : "text-muted-foreground";

export default function Opportunities() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("Toutes");
  const [type, setType] = useState("ALL");
  const [building, setBuilding] = useState("ALL");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (city !== "Toutes") params.city = city;
      if (type !== "ALL") params.opportunity_type = type;
      if (building !== "ALL") params.building_type = building;
      const { data } = await api.get("/opportunities", { params });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [q, city, type, building]);

  useEffect(() => { load(); }, [load]);

  const reset = () => { setQ(""); setCity("Toutes"); setType("ALL"); setBuilding("ALL"); };

  return (
    <div className="space-y-6" data-testid="opportunities-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Opportunités</h1>
        <p className="text-muted-foreground mt-1">Prospects et opportunités identifiés par l'IA — chaque donnée expose sa source.</p>
      </div>

      {/* Filters */}
      <Card className="p-4 bg-card border-border">
        <div className="grid md:grid-cols-5 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher entreprise, description, lieu…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="opportunity-search-input"
            />
          </div>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger data-testid="filter-city"><SelectValue /></SelectTrigger>
            <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={building} onValueChange={setBuilding}>
            <SelectTrigger data-testid="filter-building"><SelectValue /></SelectTrigger>
            <SelectContent>{BUILDINGS.map((b) => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2"><Filter className="h-3.5 w-3.5" /> {items.length} résultat{items.length > 1 ? "s" : ""}</div>
          <Button variant="ghost" size="sm" onClick={reset} data-testid="reset-filters"><X className="h-3 w-3 mr-1" /> Réinitialiser</Button>
        </div>
      </Card>

      {loading ? (
        <div className="text-center text-muted-foreground py-12">Chargement…</div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-border bg-card/40" data-testid="empty-opportunities">
          <Building2 className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-semibold mb-1">Aucune opportunité trouvée</h3>
          <p className="text-sm text-muted-foreground mb-4">Essayez d'élargir votre rayon de recherche ou de réinitialiser les filtres.</p>
          <Button variant="outline" onClick={reset}>Réinitialiser</Button>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((o) => (
            <Link key={o.opportunity_id} to={`/opportunites/${o.opportunity_id}`} data-testid={`opportunity-card-${o.opportunity_id}`}>
              <Card className="p-5 bg-card border-border card-hover h-full flex flex-col">
                <div className="flex items-start justify-between mb-3">
                  <Badge className="bg-cyan-500/10 text-cyan-300 border-cyan-500/30 text-[10px]">{o.opportunity_type.replace(/_/g, " ")}</Badge>
                  <div className="text-right">
                    <div className={`text-2xl font-display font-bold ${scoreColor(o.ai_score)}`}>{o.ai_score || "—"}</div>
                    <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-wider">Score IA</div>
                  </div>
                </div>
                <h3 className="font-semibold text-base mb-1 line-clamp-1">{o.company_name}</h3>
                <div className="text-xs text-muted-foreground flex items-center gap-1.5 mb-2">
                  <MapPin className="h-3 w-3" /> {o.city} · {o.industry}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 mb-4 flex-1">{o.description}</p>
                <div className="flex items-center justify-between pt-3 border-t border-border/70">
                  {o.estimated_value ? (
                    <span className="text-xs font-medium text-cyan-300">
                      ≈ {new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(o.estimated_value)} / an
                    </span>
                  ) : <span className="text-xs text-muted-foreground">Valeur non estimée</span>}
                  <Badge variant="outline" className="text-[10px] border-border/60">{o.source}</Badge>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
